import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, LogOut, Mail, Phone, Building, Globe, Heart } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminTurkeyDrive = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    fetch(`${API}/api/turkey-drive/submissions`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin/login'); return; } return r.json(); })
      .then(data => { if (data) setSubmissions(data.submissions || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [navigate]);

  const handleLogout = () => { localStorage.removeItem('admin_token'); navigate('/admin/login'); };

  if (loading) return <div className="min-h-screen pt-20 flex items-center justify-center"><p className="text-lg text-gray-600">Loading...</p></div>;

  return (
    <div className="min-h-screen pt-20 bg-gray-50" data-testid="admin-turkey-drive">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <Link to="/admin" className="text-sm text-gray-500 hover:text-[#E6B933] flex items-center gap-1 mb-2"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="admin-turkey-drive-title">Turkey Drive Submissions</h1>
            <p className="text-gray-500 mt-1">{submissions.length} sponsor{submissions.length !== 1 ? 's' : ''} interested</p>
          </div>
          <Button variant="outline" onClick={handleLogout} className="text-red-500 border-red-300 hover:bg-red-50"><LogOut className="w-4 h-4 mr-2" /> Logout</Button>
        </div>

        {submissions.length === 0 ? (
          <Card className="p-12 text-center">
            <Heart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Submissions Yet</h3>
            <p className="text-gray-500">Share the Turkey Drive form to start receiving sponsor interest.</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {submissions.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((sub, i) => (
              <Card key={i} className="p-6" data-testid={`td-submission-${i}`}>
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-bold text-gray-900">{sub.business_name}</h3>
                      <span className="text-xs text-gray-400">{sub.created_at ? new Date(sub.created_at).toLocaleDateString() : ''}</span>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2 text-sm text-gray-600 mb-4">
                      <div className="flex items-center gap-2"><Building className="w-4 h-4 text-gray-400" />{sub.contact_person}{sub.title ? `, ${sub.title}` : ''}</div>
                      <div className="flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" />{sub.phone}</div>
                      <div className="flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" />{sub.email}</div>
                      {sub.website && <div className="flex items-center gap-2"><Globe className="w-4 h-4 text-gray-400" />{sub.website}</div>}
                    </div>

                    <div className="mb-3">
                      <p className="text-xs text-gray-500 font-medium mb-1.5">Support Methods:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {sub.support_methods.map((m, j) => (
                          <span key={j} className="px-2.5 py-1 bg-[#E6B933]/10 text-[#E6B933] rounded-full text-xs font-medium">{m.replace(/_/g, ' ')}</span>
                        ))}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                      {sub.turkey_count && <span><strong>Turkeys:</strong> {sub.turkey_count}</span>}
                      {sub.monetary_amount && <span><strong>Monetary:</strong> {sub.monetary_amount}</span>}
                      <span><strong>Recognition:</strong> {sub.recognition === 'yes' ? 'Yes' : sub.recognition === 'no' ? 'No' : 'Contact me'}</span>
                    </div>

                    {sub.comments && <p className="text-sm text-gray-500 mt-3 italic">"{sub.comments}"</p>}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminTurkeyDrive;
